/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useMemo, useState } from 'react';

import { styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily, styleguideLetterSpacing, styleguideSpacing } from '@kaiizen/kai-shiki/core';
import { MODAL_BASE_PRIMARY_BUTTON_STYLE, MODAL_BASE_SECONDARY_BUTTON_STYLE, ModalBase } from './ModalBase';

export const TURN_HANDOFF_TITLE = 'Hand off the project';
export const TURN_HANDOFF_CONFIRM_LABEL = 'Hand Off';
export const TURN_HANDOFF_RECIPIENT_LABEL = 'Select the person to hand off';
export const TURN_HANDOFF_COMMENT_LABEL = 'Add a comment (optional)';
export const TAKE_TURN_CANCEL_LABEL = 'Nevermind';
export const TAKE_TURN_CONFIRM_LABEL = 'Start editing';

export type TurnHandoffRecipient = {
  id: string;
  name: string;
  capabilityLabel: string;
  isProjectParticipant?: boolean;
  willBeAddedToProject?: boolean;
};

export type TurnHandoffSubmission = {
  recipientId: string;
  byUserComment: string;
};

export type TurnHandoffHistoryEntry = {
  id: string;
  createdAt: Date | string;
  actorUserId?: string;
  actorName: string;
  description: string;
  recipientUserId?: string;
  recipientName?: string;
  byUserComment?: string;
};

const INTRO_STYLE = css`
  label: TURN_HANDOFF_INTRO_STYLE;
  margin: 0;
  color: ${styleguideColors.ink};
  font-size: ${pxToRem(styleguideFontSize.bodySmall)};
`;

const SECTION_STYLE = css`
  label: TURN_HANDOFF_SECTION_STYLE;
  margin-top: ${styleguideSpacing.s5}px;
`;

const HISTORY_LIST_STYLE = css`
  label: TURN_HANDOFF_HISTORY_LIST_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${styleguideSpacing.s2}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const HISTORY_ENTRY_STYLE = css`
  label: TURN_HANDOFF_HISTORY_ENTRY_STYLE;
  display: flex;
  align-items: center;
  gap: ${styleguideSpacing.s2}px;
  padding-bottom: ${styleguideSpacing.s2}px;
  border-bottom: 1px solid ${styleguideColors.stroke};

  &:last-child { padding-bottom: 0; border-bottom: 0; }
`;

const HISTORY_CHECK_STYLE = css`
  label: TURN_HANDOFF_HISTORY_CHECK_STYLE;
  display: inline-flex;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${styleguideColors.ink};
  color: ${styleguideColors.white};
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(styleguideFontSize.monoMicro)};
`;

const HISTORY_COPY_STYLE = css`
  label: TURN_HANDOFF_HISTORY_COPY_STYLE;
  flex: 1;
  margin: 0;
  color: ${styleguideColors.ink};
`;

const HISTORY_DATE_STYLE = css`
  label: TURN_HANDOFF_HISTORY_DATE_STYLE;
  flex-shrink: 0;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(styleguideFontSize.monoMicro)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoCaption};
  text-transform: uppercase;
  color: ${styleguideColors.midtone};
`;

const HISTORY_TOGGLE_STYLE = css`
  label: TURN_HANDOFF_HISTORY_TOGGLE_STYLE;
  padding: 0;
  color: ${styleguideColors.ink};
  background: transparent;
  border: 0;
  cursor: pointer;
  font: inherit;
  font-weight: ${styleguideFontWeight.bold};
  text-decoration: underline;
`;

const HISTORY_SUMMARY_STYLE = css`
  label: TURN_HANDOFF_HISTORY_SUMMARY_STYLE;
  margin: 0 0 ${styleguideSpacing.s3}px;
  color: ${styleguideColors.ink};
`;

const LABEL_STYLE = css`
  label: TURN_HANDOFF_LABEL_STYLE;
  display: block;
  margin-bottom: ${styleguideSpacing.s2}px;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(styleguideFontSize.monoCaption)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoCaption};
  text-transform: uppercase;
  color: ${styleguideColors.ink};
`;

const SEARCH_INPUT_STYLE = css`
  label: TURN_HANDOFF_SEARCH_INPUT_STYLE;
  width: 100%;
  box-sizing: border-box;
  padding: ${styleguideSpacing.s3}px;
  padding-right: calc(${styleguideSpacing.s8}px + ${styleguideSpacing.s3}px);
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
  color: ${styleguideColors.ink};
  background: ${styleguideColors.white};
  border: 1px solid ${styleguideColors.stroke};

  &::placeholder { color: ${styleguideColors.midtone}; }
  &:focus { border-color: ${styleguideColors.ink}; outline: 0; }
`;

const SEARCH_FIELD_STYLE = css`
  label: TURN_HANDOFF_SEARCH_FIELD_STYLE;
  position: relative;
`;

const CLEAR_SEARCH_BUTTON_STYLE = css`
  label: TURN_HANDOFF_CLEAR_SEARCH_BUTTON_STYLE;
  position: absolute;
  top: 50%;
  right: ${styleguideSpacing.s2}px;
  min-height: calc(${styleguideSpacing.s5}px - 2px);
  padding: 0 ${styleguideSpacing.s2}px;
  color: ${styleguideColors.ink};
  background: ${styleguideColors.white};
  border: 0;
  border-left: 1px solid ${styleguideColors.stroke};
  cursor: pointer;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(styleguideFontSize.monoMicro)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoCaption};
  text-transform: uppercase;
  transform: translateY(-50%);

  &:hover, &:focus-visible { color: ${styleguideColors.textSecondary}; outline: 0; }
  &:focus-visible { box-shadow: 0 0 0 2px ${styleguideColors.ink}; }
`;

const RECIPIENT_LIST_STYLE = css`
  label: TURN_HANDOFF_RECIPIENT_LIST_STYLE;
  max-height: calc(${styleguideSpacing.s7}px * 5);
  margin-top: ${styleguideSpacing.s2}px;
  overflow-y: auto;
`;

const RECIPIENT_LIST_CONTAINER_STYLE = css`
  label: TURN_HANDOFF_RECIPIENT_LIST_CONTAINER_STYLE;
  position: relative;
`;

const RECIPIENT_SCROLL_CUE_STYLE = css`
  label: TURN_HANDOFF_RECIPIENT_SCROLL_CUE_STYLE;
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: ${styleguideSpacing.s7}px;
  pointer-events: none;
  background: linear-gradient(to bottom, transparent, ${styleguideColors.white});
`;

const RECIPIENT_LOAD_MORE_SENTINEL_STYLE = css`
  label: TURN_HANDOFF_RECIPIENT_LOAD_MORE_SENTINEL_STYLE;
  height: ${styleguideSpacing.s2}px;
`;

const RECIPIENT_ROW_STYLE = (selected: boolean) =>
  css`
  label: TURN_HANDOFF_RECIPIENT_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${styleguideSpacing.s3}px;
  width: 100%;
  box-sizing: border-box;
  min-height: ${styleguideSpacing.s7}px;
  padding: ${styleguideSpacing.s3}px;
  background: ${selected ? styleguideColors.accent : styleguideColors.white};
  border: 0;
  border-bottom: 1px solid ${styleguideColors.stroke};
  color: ${styleguideColors.ink};
  cursor: pointer;
  text-align: left;

  &:hover { background: ${selected ? styleguideColors.accent : styleguideColors.sunken}; }
  &:focus-visible { outline: 2px solid ${styleguideColors.ink}; outline-offset: -2px; }
`;

const RECIPIENT_NAME_STYLE = css`
  label: TURN_HANDOFF_RECIPIENT_NAME_STYLE;
  flex: 1;
  min-width: 0;
  font-family: ${styleguideFontFamily.display};
  font-size: ${pxToRem(styleguideFontSize.bodyMedium)};
  font-weight: ${styleguideFontWeight.bold};
`;

const CAPABILITY_STYLE = css`
  label: TURN_HANDOFF_CAPABILITY_STYLE;
  flex-shrink: 0;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(styleguideFontSize.monoMicro)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoCaption};
  text-transform: uppercase;
  color: ${styleguideColors.midtone};
`;

const CHECKMARK_STYLE = css`
  label: TURN_HANDOFF_CHECKMARK_STYLE;
  flex-shrink: 0;
  width: ${styleguideSpacing.s3}px;
  color: ${styleguideColors.ink};
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(styleguideFontSize.bodyMedium)};
  font-weight: ${styleguideFontWeight.bold};
  text-align: center;
`;

const EMPTY_STATE_STYLE = css`
  label: TURN_HANDOFF_EMPTY_STATE_STYLE;
  margin: ${styleguideSpacing.s2}px 0 0;
  color: ${styleguideColors.textSecondary};
`;

const MARKET_RECIPIENT_NOTICE_STYLE = css`
  label: TURN_HANDOFF_MARKET_RECIPIENT_NOTICE_STYLE;
  margin: ${styleguideSpacing.s2}px 0 0;
  color: ${styleguideColors.ink};
  text-align: right;
`;

const TAKE_TURN_MESSAGE_STYLE = css`
  label: TAKE_TURN_MESSAGE_STYLE;
  margin: 0;
  color: ${styleguideColors.ink};
  font-size: ${pxToRem(styleguideFontSize.bodySmall)};
`;

const TAKE_TURN_CONFIRM_BUTTON_STYLE = css`
  ${MODAL_BASE_PRIMARY_BUTTON_STYLE};
  label: TAKE_TURN_CONFIRM_BUTTON_STYLE;
  background: ${styleguideColors.accent};
  border-color: ${styleguideColors.accent};
  color: ${styleguideColors.ink};

  &:hover { background: ${styleguideColors.accent}; border-color: ${styleguideColors.ink}; }
`;

const COMMENT_STYLE = css`
  label: TURN_HANDOFF_COMMENT_STYLE;
  display: block;
  box-sizing: border-box;
  width: 100%;
  min-height: calc(${styleguideSpacing.s8}px * 2);
  padding: ${styleguideSpacing.s3}px;
  resize: vertical;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
  color: ${styleguideColors.ink};
  background: ${styleguideColors.white};
  border: 1px solid ${styleguideColors.stroke};

  &::placeholder { color: ${styleguideColors.midtone}; }
  &:focus { border-color: ${styleguideColors.ink}; outline: 0; }
`;

const HISTORY_COMMENT_STYLE = css`
  label: TURN_HANDOFF_HISTORY_COMMENT_STYLE;
  margin: ${styleguideSpacing.s1}px 0 0;
  color: ${styleguideColors.textSecondary};
  font-size: ${pxToRem(styleguideFontSize.bodySmall)};
`;

const normalizeSearch = (value: string) => value.trim().toLocaleLowerCase();

const TURN_HANDOFF_RECIPIENT_PAGE_SIZE = 5;

const formatHistoryDate = (createdAt: Date | string) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(createdAt));

export const getVisibleTurnHandoffRecipients = (recipients: readonly TurnHandoffRecipient[], search: string) => {
  const normalizedSearch = normalizeSearch(search);
  return normalizedSearch
    ? recipients.filter(recipient => recipient.name.toLocaleLowerCase().includes(normalizedSearch))
    : recipients;
};

const HandoffIcon = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
    <path d='M21 3 10 14' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
    <path d='m21 3-7 18-4-7-7-4 18-7Z' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const TakeTurnIcon = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
    <path d='M10 3.25 17 16.75H3L10 3.25Z' stroke='currentColor' strokeWidth='1.5' strokeLinejoin='round' />
    <path d='M10 7.5v4.25M10 14.25v.25' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
  </svg>
);

/** Confirms that the current holder’s draft work will become the viewer’s turn. */
export const TakeTurnModal = ({
  currentHolderName,
  busy = false,
  onConfirm,
  onCancel,
}: {
  currentHolderName: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <ModalBase
    icon={<TakeTurnIcon />}
    title={`Take the turn from ${currentHolderName}?`}
    maxWidth={520}
    onOverlayClick={onCancel}
    actions={
      <>
        <button type='button' css={MODAL_BASE_SECONDARY_BUTTON_STYLE} disabled={busy} onClick={onCancel}>{TAKE_TURN_CANCEL_LABEL}</button>
        <button type='button' css={TAKE_TURN_CONFIRM_BUTTON_STYLE} disabled={busy} onClick={onConfirm}>{TAKE_TURN_CONFIRM_LABEL}</button>
      </>
    }
  >
    <p css={TAKE_TURN_MESSAGE_STYLE}>
      {currentHolderName} may still be working on this. You can choose to start working on this project anyway, and we will notify{' '}
      {currentHolderName}. You may hand it off to {currentHolderName} or the next person once you’re finished.
    </p>
  </ModalBase>
);

/**
 * The builder-facing handoff form deliberately owns only its draft form state.
 * Its caller supplies the workflow mutation, keeping scan/workflow persistence
 * out of a reusable widget and preventing a handoff from being confused with
 * scope approval or field-agent assignment.
 */
export const TurnHandoffModal = ({
  recipients,
  history = [],
  busy = false,
  onConfirm,
  onCancel,
}: {
  recipients: readonly TurnHandoffRecipient[];
  history?: readonly TurnHandoffHistoryEntry[];
  busy?: boolean;
  onConfirm: (submission: TurnHandoffSubmission) => void;
  onCancel: () => void;
}) => {
  const [recipientId, setRecipientId] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [comment, setComment] = useState('');
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [visibleRecipientCount, setVisibleRecipientCount] = useState(TURN_HANDOFF_RECIPIENT_PAGE_SIZE);
  const matchingRecipients = useMemo(
    () => getVisibleTurnHandoffRecipients(recipients, search),
    [recipients, search],
  );
  const visibleRecipients = matchingRecipients.slice(0, visibleRecipientCount);
  const selectedRecipient = recipients.find(recipient => recipient.id === recipientId);

  const updateSearch = (nextSearch: string) => {
    setSearch(nextSearch);
    setVisibleRecipientCount(TURN_HANDOFF_RECIPIENT_PAGE_SIZE);
    if (!nextSearch.trim() || (recipientId && !getVisibleTurnHandoffRecipients(recipients, nextSearch).some(recipient => recipient.id === recipientId))) {
      setRecipientId(undefined);
    }
  };

  const submit = () => {
    if (!recipientId) return;
    onConfirm({
      recipientId,
      byUserComment: comment.trim(),
    });
  };

  return (
    <ModalBase
      icon={<HandoffIcon />}
      title={TURN_HANDOFF_TITLE}
      centered
      maxWidth={668}
      onOverlayClick={onCancel}
      actions={
        <>
          <button type='button' css={MODAL_BASE_SECONDARY_BUTTON_STYLE} disabled={busy} onClick={onCancel}>Cancel</button>
          <button type='button' css={MODAL_BASE_PRIMARY_BUTTON_STYLE} disabled={busy || !recipientId} onClick={submit}>
            {TURN_HANDOFF_CONFIRM_LABEL}
          </button>
        </>
      }
    >
      <p css={INTRO_STYLE}>
        Handing off gives the next person responsibility for this project. Only the current work holder can edit the scope and audience documents. Only the
        designated Project Manager can give final approval.
      </p>

      {history.length
        ? (
          <div css={SECTION_STYLE}>
            <p css={HISTORY_SUMMARY_STYLE}>
              <strong>{history[0]!.actorName}</strong> was the last person who worked on this project.{' '}
              <button
                type='button'
                css={HISTORY_TOGGLE_STYLE}
                aria-expanded={historyExpanded}
                onClick={() => setHistoryExpanded(expanded => !expanded)}
              >
                {historyExpanded ? 'Hide details' : 'View handoff history'}
              </button>
            </p>
            {historyExpanded
              ? (
                <ol css={HISTORY_LIST_STYLE} aria-label='Handoff history'>
                  {history.map(entry => (
                    <li key={entry.id} css={HISTORY_ENTRY_STYLE}>
                      <span css={HISTORY_CHECK_STYLE} aria-hidden='true'>✓</span>
                      <div css={HISTORY_COPY_STYLE}>
                        <strong>{entry.actorName}</strong> {entry.description}
                        {entry.recipientName
                          ? (
                            <>
                              {' '}
                              <strong>{entry.recipientName}</strong>
                            </>
                          )
                          : null}.
                        {entry.byUserComment ? <p css={HISTORY_COMMENT_STYLE}>“{entry.byUserComment}”</p> : null}
                      </div>
                      <time css={HISTORY_DATE_STYLE} dateTime={new Date(entry.createdAt).toISOString()}>{formatHistoryDate(entry.createdAt)}</time>
                    </li>
                  ))}
                </ol>
              )
              : null}
          </div>
        )
        : null}

      <div css={SECTION_STYLE}>
        <label css={LABEL_STYLE} htmlFor='turn-handoff-search'>{TURN_HANDOFF_RECIPIENT_LABEL}</label>
        <div css={SEARCH_FIELD_STYLE}>
          <input
            id='turn-handoff-search'
            css={SEARCH_INPUT_STYLE}
            type='text'
            value={search}
            placeholder='Search by name'
            onChange={event => updateSearch(event.target.value)}
          />
          {search
            ? <button type='button' css={CLEAR_SEARCH_BUTTON_STYLE} onClick={() => updateSearch('')}>Clear search</button>
            : null}
        </div>
        {visibleRecipients.length
          ? (
            <div css={RECIPIENT_LIST_CONTAINER_STYLE}>
              <div
                css={RECIPIENT_LIST_STYLE}
                aria-label='People to hand off to'
                onScroll={event => {
                  const recipientList = event.currentTarget;
                  if (recipientList.scrollTop + recipientList.clientHeight < recipientList.scrollHeight) return;
                  setVisibleRecipientCount(count => Math.min(count + TURN_HANDOFF_RECIPIENT_PAGE_SIZE, matchingRecipients.length));
                }}
              >
                {visibleRecipients.map(recipient => {
                  const selected = recipient.id === recipientId;
                  return (
                    <button
                      key={recipient.id}
                      type='button'
                      css={RECIPIENT_ROW_STYLE(selected)}
                      aria-pressed={selected}
                      onClick={() => setRecipientId(recipient.id)}
                    >
                      <span css={RECIPIENT_NAME_STYLE}>{recipient.name}</span>
                      <span css={CAPABILITY_STYLE}>{recipient.capabilityLabel}</span>
                      <span css={CHECKMARK_STYLE} aria-hidden='true'>{selected ? '✓' : ''}</span>
                    </button>
                  );
                })}
                {visibleRecipients.length < matchingRecipients.length ? <div css={RECIPIENT_LOAD_MORE_SENTINEL_STYLE} aria-hidden='true' /> : null}
              </div>
              {visibleRecipients.length < matchingRecipients.length
                ? <div css={RECIPIENT_SCROLL_CUE_STYLE} data-testid='turn-handoff-more-recipients-cue' aria-hidden='true' />
                : null}
            </div>
          )
          : <p css={EMPTY_STATE_STYLE}>No people match that name.</p>}
        {selectedRecipient?.willBeAddedToProject
          ? <p css={MARKET_RECIPIENT_NOTICE_STYLE}>{selectedRecipient.name} will automatically be added to this project.</p>
          : null}
      </div>

      <div css={SECTION_STYLE}>
        <label css={LABEL_STYLE} htmlFor='turn-handoff-comment'>{TURN_HANDOFF_COMMENT_LABEL}</label>
        <textarea
          id='turn-handoff-comment'
          css={COMMENT_STYLE}
          value={comment}
          placeholder='What have you left open, or what should they look at first?'
          onChange={event => setComment(event.target.value)}
        />
      </div>
    </ModalBase>
  );
};
